import { useEffect, useMemo, useState } from 'react'
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline'
import DateInput from '@/components/DateInput'
import CredentialStatusCard from '@/components/CredentialStatusCard'
import {
  CloverMerchantsCard,
  PaymasterTipAndRoleSettings,
} from '@/components/payroll/PaymasterSettingsCards'
import {
  DeductionLabelsCard,
  EmployeeLocationOverridesCard,
  LocationMappingsCard,
  WagesGrossFromCard,
} from '@/components/ToolMappingSettings'
import TipCalculatorTab from '@/components/payroll/TipCalculatorTab'
import LaborAnalyticsTab from '@/components/payroll/LaborAnalyticsTab'
import JournalEntryPreview from '@/components/JournalEntryPreview'
import WarningBanner from '@/components/WarningBanner'
import AccountingToolPage from '@/components/AccountingToolPage'
import { toast } from '@/store/toastStore'
import { useQBOData } from '@/hooks/useQBOData'
import type { SelectOption } from '@/components/SearchableSelect'
import {
  confirmHomebaseRepost,
  createHomebaseDraft,
  getHomebasePayrollRuns,
  previewHomebaseRepost,
  pullAllHomebasePayrollRuns,
  pullHomebasePayrollRun,
  type HomebaseRepostPreview,
} from '@/services/homebasePayroll'
import { getSettings } from '@/services/settings'
import {
  getQboIdMappings,
  getJournalEntryDraft,
  postJournalEntry,
  updateJournalEntryLines,
  type JournalEntryLine,
} from '@/services/accounting'
import { formatApiError } from '@/utils/apiError'
import { useHomebasePayrollStore, type WizardStep } from '@/store/homebasePayrollStore'
import { PAYROLL_TABS, type PayrollTabId } from '@/pages/homebasePayrollTabs'

function formatMoney(value: number | string): string {
  const num = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(num)) return String(value)
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

function formatDateIso(value: string | null | undefined): string {
  if (!value?.trim()) return ''
  const trimmed = value.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return trimmed
  const year = parsed.getFullYear()
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const STEP_LABELS = ['Date range', 'Select run', 'Review run', 'Draft & post']

const EMPTY_DRAFT = {
  draftId: null,
  draftStatus: 'draft',
  docNumber: '',
  qboUrl: null,
  lines: [] as JournalEntryLine[],
  totalDebits: 0,
  totalCredits: 0,
  warnings: [] as string[],
}

type TabId = PayrollTabId

export default function HomebasePayroll() {
  const step = useHomebasePayrollStore((s) => s.step)
  const startDate = useHomebasePayrollStore((s) => s.startDate)
  const endDate = useHomebasePayrollStore((s) => s.endDate)
  const runs = useHomebasePayrollStore((s) => s.runs)
  const selectedRunId = useHomebasePayrollStore((s) => s.selectedRunId)
  const pullData = useHomebasePayrollStore((s) => s.pullData)
  const draftId = useHomebasePayrollStore((s) => s.draftId)
  const draftStatus = useHomebasePayrollStore((s) => s.draftStatus)
  const docNumber = useHomebasePayrollStore((s) => s.docNumber)
  const qboUrl = useHomebasePayrollStore((s) => s.qboUrl)
  const lines = useHomebasePayrollStore((s) => s.lines)
  const totalDebits = useHomebasePayrollStore((s) => s.totalDebits)
  const totalCredits = useHomebasePayrollStore((s) => s.totalCredits)
  const warnings = useHomebasePayrollStore((s) => s.warnings)
  const setStep = useHomebasePayrollStore((s) => s.setStep)
  const setStartDate = useHomebasePayrollStore((s) => s.setStartDate)
  const setEndDate = useHomebasePayrollStore((s) => s.setEndDate)
  const setRuns = useHomebasePayrollStore((s) => s.setRuns)
  const setSelectedRunId = useHomebasePayrollStore((s) => s.setSelectedRunId)
  const setPullData = useHomebasePayrollStore((s) => s.setPullData)
  const setDraft = useHomebasePayrollStore((s) => s.setDraft)
  const setDraftPosted = useHomebasePayrollStore((s) => s.setDraftPosted)
  const setLines = useHomebasePayrollStore((s) => s.setLines)
  const setDraftTotals = useHomebasePayrollStore((s) => s.setDraftTotals)
  const expireIfStale = useHomebasePayrollStore((s) => s.expireIfStale)
  const resetWorkflow = useHomebasePayrollStore((s) => s.reset)

  const [tab, setTab] = useState<TabId>('allocate')
  const [loadingRuns, setLoadingRuns] = useState(false)
  const [pullingAll, setPullingAll] = useState(false)
  const [pulling, setPulling] = useState(false)
  const [wagesGrossFrom, setWagesGrossFrom] = useState<string | null>(null)
  const [repostPreview, setRepostPreview] = useState<HomebaseRepostPreview | null>(null)
  const [reposting, setReposting] = useState(false)
  const [creatingDraft, setCreatingDraft] = useState(false)
  const [savingLines, setSavingLines] = useState(false)
  const [posting, setPosting] = useState(false)
  const [accountOptions, setAccountOptions] = useState<SelectOption[]>([])

  const { locations } = useQBOData()
  const locationOptions: SelectOption[] = useMemo(
    () => locations.map((l) => ({ id: l.id, label: l.name })),
    [locations]
  )

  useEffect(() => {
    async function loadQboMappings() {
      try {
        const response = await getQboIdMappings()
        setAccountOptions(
          response.mappings
            .filter((mapping) => mapping.mapping_type === 'account')
            .map((mapping) => ({ id: mapping.qbo_id, label: mapping.display_name }))
            .sort((a, b) => a.label.localeCompare(b.label))
        )
      } catch (err) {
        console.error('Failed to load QBO account mappings:', err)
      }
    }
    void loadQboMappings()
  }, [])

  const missingLocations = useMemo(
    () => lines.some((l) => !l.department_id && !l.department_name?.trim()),
    [lines]
  )

  const balanced = useMemo(() => {
    const debits = Number(totalDebits)
    const credits = Number(totalCredits)
    return Math.abs(debits - credits) < 0.005
  }, [totalDebits, totalCredits])

  useEffect(() => {
    expireIfStale()
  }, [expireIfStale])

  useEffect(() => {
    void getSettings()
      .then((data) => {
        const raw = data.settings.homebase_payroll_wages_gross_from?.value?.trim()
        setWagesGrossFrom(raw || null)
      })
      .catch((err) => {
        console.error('Failed to load wages gross setting:', err)
      })
  }, [])

  useEffect(() => {
    if (step !== 4 || !draftId) return

    let cancelled = false
    void getJournalEntryDraft(draftId)
      .then((draft) => {
        if (cancelled) return
        setDraftPosted({
          draftStatus: draft.status,
          qboUrl: draft.qbo_url ?? null,
        })
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to refresh payroll draft status from QuickBooks:', err)
      })

    return () => {
      cancelled = true
    }
  }, [step, draftId, setDraftPosted])

  function handleRetryAfterTokenSave() {
    if (startDate && endDate) {
      void handleLoadRuns()
    }
  }

  async function handleLoadRuns() {
    if (!startDate || !endDate) {
      toast.error('Select a start and end date')
      return
    }
    try {
      setLoadingRuns(true)
      const data = await getHomebasePayrollRuns(startDate, endDate)
      setRuns(data.runs)
      setSelectedRunId(null)
      setPullData(null)
      setDraft(EMPTY_DRAFT)
      setStep(2)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load payroll runs'))
    } finally {
      setLoadingRuns(false)
    }
  }

  async function handlePullAllRuns() {
    try {
      setPullingAll(true)
      const result = await pullAllHomebasePayrollRuns('2025-04-01')
      toast.success(
        `Pull all complete: ${result.pulled} pulled, ${result.skipped} skipped, ${result.errors.length} errors`
      )
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to pull all payroll runs'))
    } finally {
      setPullingAll(false)
    }
  }

  const selectedRun = useMemo(
    () => runs.find((run) => run.id === selectedRunId) ?? null,
    [runs, selectedRunId]
  )

  const canRepostSelectedRun = useMemo(() => {
    if (!selectedRun?.posted_je_id || !selectedRun.payday || !wagesGrossFrom) return false
    const paydayIso = formatDateIso(selectedRun.payday)
    if (!paydayIso) return false
    return paydayIso >= wagesGrossFrom
  }, [selectedRun, wagesGrossFrom])

  async function handleOpenRepostPreview() {
    if (!selectedRunId) return
    try {
      setReposting(true)
      const preview = await previewHomebaseRepost(selectedRunId)
      setRepostPreview(preview)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load re-post preview'))
    } finally {
      setReposting(false)
    }
  }

  async function handleConfirmRepost() {
    if (!selectedRunId) return
    try {
      setReposting(true)
      await confirmHomebaseRepost(selectedRunId)
      toast.success('Payroll journal entry re-posted on new basis')
      setRepostPreview(null)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to re-post payroll journal entry'))
    } finally {
      setReposting(false)
    }
  }

  async function handlePullRun(runId: string) {
    try {
      setPulling(true)
      setSelectedRunId(runId)
      const data = await pullHomebasePayrollRun(runId)
      setPullData(data)
      setStep(3)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to pull payroll run'))
    } finally {
      setPulling(false)
    }
  }

  async function handleCreateDraft() {
    if (!selectedRunId) return
    try {
      setCreatingDraft(true)
      const result = await createHomebaseDraft({ run_id: selectedRunId })
      setDraft({
        draftId: result.id,
        draftStatus: result.status,
        docNumber: result.doc_number,
        qboUrl: null,
        lines: result.lines,
        totalDebits: result.total_debits,
        totalCredits: result.total_credits,
        warnings: result.warnings,
      })
      setStep(4)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to create draft'))
    } finally {
      setCreatingDraft(false)
    }
  }

  async function handleLinesChange(next: JournalEntryLine[]) {
    setLines(next)
    if (!draftId) return
    try {
      setSavingLines(true)
      const updated = await updateJournalEntryLines(draftId, next)
      setLines(updated.lines)
      setDraftTotals(updated.total_debits, updated.total_credits)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save line changes'))
    } finally {
      setSavingLines(false)
    }
  }

  async function handlePost() {
    if (!draftId) return
    try {
      setPosting(true)
      const response = await postJournalEntry(draftId)
      setDraftPosted({
        draftStatus: response.draft.status,
        qboUrl: response.draft.qbo_url ?? null,
      })
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to post journal entry'))
    } finally {
      setPosting(false)
    }
  }

  function handleResetWorkflow() {
    resetWorkflow()
  }

  function handleDateChange(which: 'start' | 'end', nextValue: string) {
    if (which === 'start') setStartDate(nextValue)
    else setEndDate(nextValue)
    if (step === 1) {
      setRuns([])
      setSelectedRunId(null)
      setPullData(null)
      setDraft(EMPTY_DRAFT)
    }
  }

  return (
    <AccountingToolPage
      title="Payroll"
      description="Pull payroll runs from Homebase and create allocation journal entries"
      scrollContent={tab === 'settings' || tab === 'tips' || tab === 'allocate'}
      headerExtra={
        <div className="overflow-x-auto">
          <div
            className="inline-flex min-w-max gap-2 rounded-lg bg-gray-200 p-1"
            role="tablist"
            aria-label="Payroll sections"
          >
            {PAYROLL_TABS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`relative whitespace-nowrap rounded-md px-4 py-1.5 text-sm font-medium transition-all ${
                  tab === id
                    ? 'bg-white text-brand-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      }
    >
      {tab === 'settings' ? (
        <div className="space-y-4">
          <LocationMappingsCard />
          <CloverMerchantsCard />
          <EmployeeLocationOverridesCard />
          <PaymasterTipAndRoleSettings />
          <DeductionLabelsCard />
          <WagesGrossFromCard />
        </div>
      ) : tab === 'tips' ? (
        <TipCalculatorTab />
      ) : tab === 'labor' ? (
        <LaborAnalyticsTab />
      ) : (
        <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs font-medium" aria-label="Progress">
          {STEP_LABELS.map((label, i) => {
            const stepNumber = (i + 1) as WizardStep
            const isCurrent = step === stepNumber
            const isComplete = step > stepNumber
            return (
              <div key={label} className="flex items-center gap-1.5">
                {i > 0 ? <ChevronRightIcon className="h-3.5 w-3.5 shrink-0 text-gray-300" /> : null}
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 transition-colors ${
                    isCurrent
                      ? 'font-medium text-white shadow-sm'
                      : isComplete
                        ? 'bg-brand-50 text-brand-700'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                  style={isCurrent ? { background: 'var(--gradient-primary)' } : undefined}
                >
                  {isComplete ? (
                    <CheckIcon className="h-3 w-3 shrink-0" />
                  ) : (
                    <span>{stepNumber}.</span>
                  )}
                  {label}
                </span>
              </div>
            )
          })}
        </nav>
        {step > 1 ? (
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={handleResetWorkflow}
          >
            <ArrowUturnLeftIcon className="h-4 w-4" />
            Start over
          </button>
        ) : null}
      </div>

      {step === 1 ? (
        <>
        <CredentialStatusCard
          service="homebase"
          showSettingsLink={false}
          onGrabSuccess={handleRetryAfterTokenSave}
        />
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="container-title">Payroll date range</h2>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-600">Start</label>
              <DateInput
                value={startDate}
                onChange={(v) => handleDateChange('start', v)}
                dateFormat="iso"
                className="input text-sm"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-600">End</label>
              <DateInput
                value={endDate}
                onChange={(v) => handleDateChange('end', v)}
                dateFormat="iso"
                className="input text-sm"
              />
            </div>
            <button
              type="button"
              className="btn btn-primary text-sm"
              onClick={() => void handleLoadRuns()}
              disabled={loadingRuns}
            >
              <ArrowPathIcon className={`h-4 w-4 ${loadingRuns ? 'animate-spin' : ''}`} />
              {loadingRuns ? 'Loading…' : 'Load runs'}
            </button>
            <button
              type="button"
              className="btn btn-secondary text-sm"
              onClick={() => void handlePullAllRuns()}
              disabled={pullingAll}
            >
              <ArrowPathIcon className={`h-4 w-4 ${pullingAll ? 'animate-spin' : ''}`} />
              {pullingAll ? 'Pulling…' : 'Pull all runs since Apr 2025'}
            </button>
          </div>
        </div>
        </>
      ) : null}

      {step >= 2 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="container-title">Payroll runs</h2>
            {canRepostSelectedRun ? (
              <button
                type="button"
                className="btn btn-secondary text-sm"
                onClick={() => void handleOpenRepostPreview()}
                disabled={reposting}
              >
                {reposting ? 'Loading…' : 'Re-post on new basis'}
              </button>
            ) : null}
          </div>
          {runs.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">No runs in this date range.</p>
          ) : (
            <div className="table-scroll-mobile mt-3 overflow-x-auto rounded-lg border border-gray-200">
              <table className="table-forecast border-separate border-spacing-0 min-w-full">
                <thead>
                  <tr>
                    <th>Pay date</th>
                    <th>Payments</th>
                    <th>Total cost</th>
                    <th>Status</th>
                    <th className="w-10" />
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr key={run.id} className={selectedRunId === run.id ? 'bg-brand-50/40' : ''}>
                      <td>{formatDateIso(run.payday) || '—'}</td>
                      <td>{run.payments_count ?? '—'}</td>
                      <td>{run.total_cost || '—'}</td>
                      <td>
                        {run.status ? (
                          <span className="badge badge-neutral capitalize">{run.status}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-secondary text-sm"
                          onClick={() => void handlePullRun(run.id)}
                          disabled={pulling && selectedRunId === run.id}
                        >
                          {pulling && selectedRunId === run.id ? 'Pulling…' : 'Select'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {step >= 3 && pullData ? (
        <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="container-title">Payroll run summary</h2>
            {pullData.payday || pullData.txn_date ? (
              <p className="text-xs text-gray-500">
                Payday{' '}
                <span className="font-medium text-gray-700">
                  {formatDateIso(pullData.txn_date ?? pullData.payday)}
                </span>
              </p>
            ) : null}
          </div>
          <div className="rounded-lg bg-gray-50/90 p-4">
            <div className="space-y-2">
              {pullData.summary_rows.map((row) => (
                <div
                  key={row.label}
                  className={`grid grid-cols-[1fr_auto] items-baseline gap-x-4 gap-y-1 text-sm ${
                    row.label === 'Total cost' ? 'border-t border-gray-200/80 pt-2 font-medium' : ''
                  }`}
                >
                  <span className="text-gray-500">{row.label}</span>
                  <span className="text-right text-gray-900">{formatMoney(row.amount)}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-500">
            {pullData.report_rows.length} employee location record
            {pullData.report_rows.length === 1 ? '' : 's'} pulled from the location breakdown report.
          </p>
          <button
            type="button"
            className="btn btn-primary text-sm"
            onClick={() => void handleCreateDraft()}
            disabled={creatingDraft}
          >
            {creatingDraft ? 'Building draft…' : 'Create draft journal entry'}
          </button>
        </div>
      ) : null}

      {step === 4 && lines.length > 0 ? (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div
            className={`flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4 ${
              draftStatus === 'posted'
                ? 'border-emerald-200 bg-emerald-50/70'
                : 'border-gray-200'
            }`}
          >
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {pullData?.payday || pullData?.txn_date
                  ? `Payroll — ${formatDateIso(pullData.txn_date ?? pullData.payday)}`
                  : 'Draft journal entry'}
              </h2>
              <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                {docNumber ? (
                  <code
                    className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${
                      draftStatus === 'posted'
                        ? 'bg-emerald-100/80 text-emerald-900'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {docNumber}
                  </code>
                ) : null}
                {draftStatus === 'posted' && qboUrl ? (
                  <a
                    href={qboUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-0.5 font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                  >
                    View in QuickBooks
                    <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  </a>
                ) : null}
              </p>
            </div>
            {draftStatus === 'posted' ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200">
                <CheckCircleIcon className="h-4 w-4" />
                Posted to QuickBooks
              </span>
            ) : (
              <button
                type="button"
                className="btn btn-primary text-sm"
                onClick={() => void handlePost()}
                disabled={posting || !balanced || missingLocations || savingLines}
              >
                {posting ? 'Posting…' : 'Post to QuickBooks'}
              </button>
            )}
          </div>

          <div className="space-y-4 p-5">
            {missingLocations ? (
              <WarningBanner message="Assign a location on every line before posting." />
            ) : null}
            {warnings.map((w, i) => (
              <WarningBanner key={i} message={w} />
            ))}
            <JournalEntryPreview
              lines={lines}
              variant="split"
              hideHeader
              editableAmounts
              editableLines
              editableLocations
              accountOptions={accountOptions}
              locationOptions={locationOptions}
              onLinesChange={(next) => void handleLinesChange(next)}
            />
            {savingLines ? (
              <p className="text-xs text-gray-500">Saving line changes…</p>
            ) : null}
          </div>
        </section>
      ) : null}
        </>
      )}

      {repostPreview ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setRepostPreview(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="repost-preview-title"
            className="relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-gray-200 px-5 py-4">
              <h3 id="repost-preview-title" className="text-sm font-semibold text-gray-900">
                Re-post on new basis
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Bank line stays at {formatMoney(repostPreview.bank_amount)}.
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              <div className="space-y-4 text-sm text-gray-700">
                {Object.keys(repostPreview.new_wages_by_store).map((store) => (
                  <div key={store} className="rounded-lg border border-gray-200 p-3">
                    <p className="font-medium text-gray-900">{store}</p>
                    <p>
                      Wages: {formatMoney(repostPreview.old_wages_by_store[store] ?? 0)} →{' '}
                      {formatMoney(repostPreview.new_wages_by_store[store] ?? 0)}
                    </p>
                    <p>
                      Benefits credit: {formatMoney(repostPreview.old_benefits_by_store[store] ?? 0)} →{' '}
                      {formatMoney(repostPreview.new_benefits_by_store[store] ?? 0)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
              <button
                type="button"
                className="btn btn-secondary text-sm"
                onClick={() => setRepostPreview(null)}
                disabled={reposting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary text-sm"
                onClick={() => void handleConfirmRepost()}
                disabled={reposting}
              >
                {reposting ? 'Re-posting…' : 'Confirm re-post'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AccountingToolPage>
  )
}
